import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["bordered", "children", "condensed", "flush", "height", "maxHeight", "hover", "responsive", "ScrollContainer", "truncated", "responsiveWrapperTokenProps"];
import { forwardRef } from 'react';
import styled, { isTrellisTheme } from 'foundations-theming/utils';
import { Section } from '../Section';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { ScrollContainer as ScrollContainerComponent } from '../ScrollContainer';
import { usePanelContext } from '../../context';
import { useTableStyles } from './Table.styles';
import { useTableContext } from './TableContext';
import { useCardContext } from '../Card/CardContext';
import { deepMerge } from '../../utils/deepMerge';
import { CSS_SPECIFICITY } from '../../constants/CSSSpecificity';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledTable = styled.table.withConfig({
  displayName: "Table__StyledTable",
  componentId: "sc-1hlyihh-0"
})(({
  theme,
  $cssStyles,
  $styleOverrides,
  $flush,
  $bordered,
  $condensed,
  $hover,
  $responsive,
  $truncated,
  $inPanel,
  $inCard
}) => {
  const {
    tableHeaderHeight,
    tableCellHeight,
    tableCellHeightCompact,
    defaultPadding,
    condensedPadding,
    tableCellVerticalAlignment
  } = useTableStyles();
  const isTrellis = isTrellisTheme(theme);
  return deepMerge(Object.assign({
    fontSize: theme.typography['body-100'].fontSize,
    backgroundColor: theme.color['fill-field-default-alt'],
    borderCollapse: 'collapse',
    width: '100%'
  }, ($flush || $responsive) && {
    [CSS_SPECIFICITY.OVERRIDE]: {
      marginBlockEnd: theme.space['0']
    }
  }, $bordered !== false && {
    borderColor: theme.color['border-transitional-core-subtle'],
    borderStyle: 'solid',
    borderWidth: theme.borderWidth['100'],
    borderTopWidth: theme.borderWidth['0']
  }, {
    '& th, & td': {
      borderColor: theme.color['border-transitional-core-subtle'],
      borderStyle: 'solid',
      borderWidth: theme.borderWidth['0'],
      borderTopWidth: theme.borderWidth['100'],
      lineHeight: 'normal',
      verticalAlign: 'middle'
    },
    '& thead th': Object.assign({}, condensedPadding, {
      fontWeight: theme.typography['heading-200'].fontWeight,
      fontSize: theme.typography['heading-25'].fontSize,
      backgroundColor: theme.color['specialty-table-header-default'],
      color: theme.color['text-core-default'],
      blockSize: tableHeaderHeight,
      textAlign: 'left',
      textTransform: isTrellis ? 'capitalize' : 'uppercase',
      verticalAlign: tableCellVerticalAlignment,
      paddingBlockEnd: theme.space['100']
    }, isTrellis && {
      '&:not(:last-child)': {
        borderInlineEnd: `${theme.borderWidth['100']} solid ${theme.color['border-transitional-core-subtle']}`
      }
    }),
    '& tfoot td, & tfoot th': {
      fontWeight: theme.typography['heading-50'].fontWeight,
      blockSize: tableCellHeight,
      paddingBlock: theme.space['400'],
      paddingInline: theme.space['600'],
      textAlign: 'left'
    },
    '& tbody td': Object.assign({
      hyphens: 'auto',
      overflowWrap: 'break-word',
      // Reminder: prevent overflow in Chrome 55+ (issue UIComponents#1494)
      verticalAlign: tableCellVerticalAlignment,
      blockSize: $condensed ? tableCellHeightCompact : tableCellHeight
    }, $condensed ? condensedPadding : defaultPadding)
  }, $hover && {
    tbody: {
      tr: {
        'td, th': {
          transition: 'background-color 150ms ease-out'
        }
      },
      'tr:hover': {
        'td, th': {
          backgroundColor: theme.color['fill-field-hover']
        }
      }
    }
  }, $truncated && {
    tableLayout: 'fixed',
    '& td, & th': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  }, {
    // Override border-collapse when TableHeader has sticky={true} for sticky header
    '&:has(thead[data-sticky-header="true"])': {
      borderCollapse: 'separate',
      borderSpacing: '0',
      // Setting border-collapse: separate to allow for sticky TableHead means we need to reset border-width
      // to 0 for the nested cells to ensure that we don't have duplicate borders
      '& th:first-child, & td:first-child': {
        borderInlineStartWidth: theme.borderWidth['0']
      },
      // Remove top border from first tbody row to prevent doubling with sticky header border
      '& tbody tr:first-child td': {
        borderTopWidth: '0'
      }
    }
  }, $inPanel && $bordered !== true && {
    borderColor: theme.color['border-transitional-core-subtle'],
    borderStyle: 'solid',
    borderWidth: theme.borderWidth['0'],
    borderBottomWidth: theme.borderWidth['100']
  }, $inCard && $bordered !== true && {
    border: 'none',
    borderRadius: 'inherit',
    'thead th': {
      backgroundColor: theme.color['fill-field-default-alt'],
      borderTop: 'none'
    }
  }), $styleOverrides, $cssStyles);
});
export const Table = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      bordered = 'auto',
      children,
      condensed = false,
      flush = false,
      height,
      maxHeight,
      hover = true,
      responsive = true,
      ScrollContainer = ScrollContainerComponent,
      truncated = false,
      responsiveWrapperTokenProps = {}
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    domProps,
    tokenProps,
    callbackProps,
    restProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const {
    tableVariantStyleOverrides
  } = useTableContext();
  const {
    inPanel
  } = usePanelContext();
  const {
    inCard
  } = useCardContext();
  const renderedTable = /*#__PURE__*/_jsx(StyledTable, Object.assign({}, domProps, callbackProps, restProps, {
    ref: ref,
    $bordered: bordered,
    $condensed: condensed,
    $flush: flush,
    $hover: hover,
    $responsive: responsive,
    $truncated: truncated,
    $cssStyles: cssStyles,
    $styleOverrides: tableVariantStyleOverrides !== null && tableVariantStyleOverrides !== void 0 ? tableVariantStyleOverrides : {},
    $inPanel: inPanel,
    $inCard: inCard,
    children: children
  }));
  return responsive ? /*#__PURE__*/_jsx(Section, Object.assign({
    flush: flush,
    height: height
  }, responsiveWrapperTokenProps, {
    children: /*#__PURE__*/_jsx(ScrollContainer, {
      scrollDirection: height != null || maxHeight != null ? 'both' : 'horizontal',
      maxHeight: maxHeight != null ? maxHeight : height,
      children: renderedTable
    })
  })) : renderedTable;
});
Table.displayName = 'Table';